import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ShieldAlert, ArrowLeft } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

export const RiskDisclaimer: React.FC = () => {
    const location = useLocation();
    const isFromSignup = new URLSearchParams(location.search).get('source') === 'signup';

    return (
        <div className="min-h-screen bg-finance-darker py-20 px-4">
            <Helmet>
                <title>Risk Disclaimer — ForexFundAI</title>
                <meta name="description" content="Institutional Risk Disclosure: Trading Forex and Gold involves significant financial risk. Understand the potential for loss before using ForexFund AI analytics." />
                <link rel="canonical" href="https://www.forexfundai.com/disclaimer" />
            </Helmet>
            <div className="max-w-4xl mx-auto bg-finance-card border-2 border-red-900/30 rounded-3xl p-8 sm:p-12 shadow-2xl relative overflow-hidden text-left">
                <div className="absolute top-0 left-0 w-full h-1 bg-red-600"></div>

                <div className="flex items-center gap-4 mb-8">
                    <div className="p-3 bg-red-500/10 rounded-2xl">
                        <ShieldAlert className="w-8 h-8 text-red-500" />
                    </div>
                    <h1 className="text-4xl font-black text-white uppercase tracking-tight">Risk Disclaimer</h1>
                </div>

                <div className="space-y-8 text-gray-300 leading-relaxed mb-12">
                    <div className="p-6 bg-red-500/5 border border-red-500/10 rounded-2xl mb-8">
                        <p className="text-white font-bold text-center">
                            WARNING: TRADING FOREIGN EXCHANGE ON MARGIN CARRIES A HIGH LEVEL OF RISK AND MAY NOT BE SUITABLE FOR ALL INVESTORS.
                        </p>
                    </div>

                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">1. High Risk Warning & Financial Loss</h2>
                        <p>
                            Trading foreign exchange (Forex), commodities, and cryptocurrencies on margin carries a <strong>substantial level of risk</strong> and may not be suitable for all investors. The high degree of leverage can work against you as well as for you. Before deciding to invest, you should carefully consider your investment objectives, level of experience, and risk appetite. <strong>You could sustain a loss of some or all of your initial investment; do not invest money that you cannot afford to lose.</strong>
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">2. Not Financial Advice (NFA)</h2>
                        <p>
                            ForexFund AI is a technology-driven analytical tool and educational information provider. <strong>We are not financial advisors.</strong> Any signals, reports, market sentiment data, or insights provided through the Service are for informational and educational purposes only and do <strong>NOT</strong> constitute investment advice or a recommendation to buy or sell any financial instrument. All users are encouraged to perform their own due diligence before entering any trade.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">3. Accuracy & Market Volatility</h2>
                        <p>
                            The financial markets are highly volatile. While we strive for maximum accuracy using advanced AI quant scanners, market conditions can shift rapidly. ForexFund AI does not guarantee the accuracy, completeness, or timeliness of the information provided and will not accept liability for any loss or damage which may arise directly or indirectly from the content.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">4. Hypothetical Performance & Past Results</h2>
                        <p>
                            Hypothetical or simulated performance results have certain inherent limitations. Unlike an actual performance record, simulated results do not represent actual trading. Past performance is <strong>not indicative of future results</strong>. No representation is being made that any account will or is likely to achieve profits or losses similar to those shown on this website.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">5. Technology & Execution Risks</h2>
                        <p>
                            Trading via digital platforms involves risks including, but not limited to, the failure of hardware, software, and internet connection. ForexFund AI is not responsible for communication failures, distortions, or delays. Use of automated execution or copying signals is at the user's own risk.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">6. Third-Party Compliance</h2>
                        <p>
                            Users must ensure that their use of this Service and their trading activities comply with the laws and regulations of their local jurisdiction. ForexFund AI is not responsible for any legal or regulatory issues arising from the user's trading activities or use of the platform.
                        </p>
                    </section>
                </div>

                <div className="pt-8 border-t border-gray-800 flex justify-center">
                    <Link
                        to={isFromSignup ? "/login?view=signup" : "/"}
                        className="flex items-center gap-2 px-6 py-3 bg-gray-900 border border-gray-800 rounded-xl text-white font-bold hover:bg-gray-800 hover:border-gray-700 transition-all group"
                    >
                        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                        {isFromSignup ? "Back to Sign Up" : "Back to Home"}
                    </Link>
                </div>
            </div>
        </div>
    );
};
